"use client";

import { useEffect } from "react";
import { useCommandPaletteStore } from "../_logic/store";

/**
 * Binds Cmd+K / Ctrl+K to toggle the command palette.
 *
 * Mounted from the Enterprise shell under `/dashboard`, and from
 * `StandaloneCommandPalette` on `/create` (ENG-835). Where it mounts is
 * deliberate and load-bearing: Cmd+K is already bound by the AVA inbox
 * (`useInboxKeyboard`, `useInboxDetailKeyboard`) and by the public FAQ page, and
 * bare `k` is a vim-style navigation key in the inbox. This handler must not
 * exist on those surfaces, so there is nothing to arbitrate.
 *
 * Before adding a third mount, check the new route the same way: grep it for
 * `metaKey`/`ctrlKey` handlers, and check nothing there portals an overlay above
 * `PALETTE_Z_INDEX`, which carries the layer list.
 *
 * Escape is handled by the dialog itself rather than here, so it cannot close
 * the palette from underneath another overlay.
 *
 * There is deliberately no "is the user typing" guard. An earlier version
 * ignored the hotkey whenever focus sat in an input, which made the palette
 * unreachable on `/dashboard/chat`: that page autofocuses its composer, so
 * Cmd+K there did nothing at all. The guard was written for bare `k` (a
 * vim-style key in the AVA inbox) and never applied here anyway, since this
 * handler requires a modifier and never mounts on those surfaces. Nothing under
 * `/dashboard` binds Cmd+K for anything else, and opening over a half-typed
 * form costs an Escape, which is how every other palette behaves.
 */
export const useCommandPaletteHotkey = () => {
  // Hooks
  const toggle = useCommandPaletteStore((state) => state.toggle);

  // Effects
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key.toLowerCase() !== "k") return;
      if (!event.metaKey && !event.ctrlKey) return;
      // Let the browser keep Cmd+Shift+K and Cmd+Alt+K for its own devtools.
      if (event.shiftKey || event.altKey) return;
      event.preventDefault();
      toggle("hotkey");
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [toggle]);
};
